import {
  IntensityLevel,
  Plan_ExercisePath,
  Plan_SubSlot,
  parseSubslotIdAndOffset,
} from '@cf-shared/data-model/fitness-plan';
import { Log_WorkoutExercise, Log_WorkoutSet, setTypeHasNoTargetPrescription } from '@cf-shared/data-model/log-workout';
import { LogRecordState } from '@cf-shared/data-model/user-log';
import { SetType } from '@cf-shared/enums';
import { TrainingLevel } from '@cf-shared/data-model/training-level';
import { getIntensityForSubSlot } from '@cf-shared/workout-solver-intensity-constants';
import { deepClone } from '@cf-shared/deep-helpers';

/**
 * #4614: which sub-slot should a logged exercise be DISPLAYED with?
 *
 * There are three candidates, tried in this order:
 *
 *  - the LIVE entry in the program, `plan.subSlots[logExercise.subSlotId]`. This is what the
 *    user's program says today, and it is the object the app writes progression back into.
 *  - the SNAPSHOT the workout log carries, `logExercise.planSubslotBackup`. This is a copy of
 *    the live entry taken at the moment the user concluded the first set of that exercise, and
 *    re-taken if the user then switches that exercise permanently, so it keeps describing the
 *    exercise the session is actually on (df-4693ac7f6451). It only exists once a set has been
 *    concluded — an exercise nobody ever started has none.
 *  - a sub-slot REBUILT from the rest of the workout log, for a session logged before the
 *    snapshot existed. `rebuildDisplaySubSlotFromLog` below owns that, and its own comment
 *    says what it reads and when it refuses.
 *
 * The rule: the live entry wins whenever the program still has one, the snapshot stands in when
 * it does not, and the rebuild stands in when there is no snapshot either. An exercise the user
 * removed in Edit Workouts has no live entry any more, and before #4614 that made it disappear
 * from every finished session that contained it — the display looked it up, found nothing, and
 * silently skipped it.
 *
 * The fallback is not only for finished workouts. `mergeWorkoutExercisesWithPlan` deliberately
 * keeps an exercise the user has already started even after it is removed from the program, so
 * a session still in progress can reach here with no live entry too.
 *
 * **`preferSnapshot` turns the first two candidates around, and only a FINISHED workout asks
 * for it** (#4939). A finished session is a historical record, so it must keep showing what it
 * showed at the time even after the user edits the program. Before #4939 this could not be
 * done: `planSubSlot` was not only what the screen drew from, it was also the object the app
 * wrote back into the program when the user answered a plateau or volume question or
 * re-concluded a past set, so preferring the snapshot fed old settings into those writes — the
 * plateau card stopped being answerable at all (the pointer it reads is written to the live
 * entry after the snapshot is taken, so a snapshot never carries it), and answering a volume
 * question wrote that old session's target weights and set counts over the current program. It
 * was withdrawn for that reason. What makes it safe now is that the write paths have a live
 * target of their own, `UiExercise.livePlanSubSlot`, and the paths that write a whole sub-slot
 * object into the program refuse to run from a historical copy. Read
 * `design-docs/exercise-replacement.md` before changing any of that.
 *
 * A session still IN PROGRESS must never ask for it. Concluding the first set writes progression
 * into this object and then attaches it to the program, plateau detection reads the pointer the
 * live entry carries, and raising a volume question compares against the program's current set
 * count — all of which need today's entry, not a copy of it.
 *
 * **`missingFromPlan` keeps meaning "the program has no entry under this id", and nothing
 * else.** Preferring the snapshot does not change that answer in either direction: it stays
 * false while the program still holds the entry, and true when the program has dropped it, on
 * exactly the same test as before. It has to, because it drives repair telemetry
 * (`reportDisplaySubSlotMissingFromPlan`) and the refusals that stop a historical copy being
 * written back into a program that no longer wants it. What says "this copy is old" is a
 * separate flag the caller sets, `UiExercise.planSubSlotIsHistorical` — the two are different
 * facts and collapsing them loses one of them.
 */

/** Where the sub-slot a logged exercise is drawn with actually came from. */
export enum DisplaySubSlotSource {
  /** the live entry in the user's program — the ordinary case */
  LivePlan = 'live-plan',
  /** the copy the workout log took when the first set was concluded */
  LogSnapshot = 'log-snapshot',
  /** built from the rest of the workout log, for a session logged before snapshots existed */
  RebuiltFromLog = 'rebuilt-from-log',
  /** nothing usable was found, and the caller drops the exercise */
  None = 'none',
}

export interface ResolvedDisplaySubSlot {
  /** the sub-slot to draw with, or undefined when no candidate could be produced */
  subSlot?: Plan_SubSlot;
  /** which of the three candidates `subSlot` came from */
  source: DisplaySubSlotSource;
  /** true when the program no longer holds an entry under this id at all */
  missingFromPlan: boolean;
}

export function resolveDisplaySubSlot(
  planSubSlots: { [subSlotId: string]: Plan_SubSlot } | undefined,
  logExercise: Log_WorkoutExercise,
  preferSnapshot = false,
): ResolvedDisplaySubSlot {
  const live = planSubSlots?.[logExercise.subSlotId];

  // #4939: a finished workout draws from its own snapshot when it has one, so that editing the
  // program later cannot change what an old session shows. `missingFromPlan` is still answered
  // by the same test as every other branch — does the program hold an entry under this id — so
  // it is false in the ordinary case and true for an exercise the user has removed, which is
  // the #4614 case and is common. Preferring the snapshot must not change that answer: the
  // write refusals and the repair telemetry both read it.
  if (preferSnapshot && logExercise.planSubslotBackup) {
    return {
      subSlot: logExercise.planSubslotBackup,
      source: DisplaySubSlotSource.LogSnapshot,
      missingFromPlan: !live,
    };
  }

  if (live) {
    return { subSlot: live, source: DisplaySubSlotSource.LivePlan, missingFromPlan: false };
  }
  // the program dropped this exercise — the snapshot is the next-best record of how it was set up
  const snapshot = logExercise.planSubslotBackup;
  if (snapshot) {
    return { subSlot: snapshot, source: DisplaySubSlotSource.LogSnapshot, missingFromPlan: true };
  }
  // no snapshot either: this session predates the snapshot. Rebuild from what the log does carry.
  const rebuilt = rebuildDisplaySubSlotFromLog(logExercise);
  if (rebuilt) {
    return { subSlot: rebuilt, source: DisplaySubSlotSource.RebuiltFromLog, missingFromPlan: true };
  }
  return { subSlot: undefined, source: DisplaySubSlotSource.None, missingFromPlan: true };
}

/**
 * #4939: what date should the snapshot a session takes of its exercise carry?
 *
 * The snapshot is that session's own record, so it has to carry that session's own moment.
 * Getting this wrong is silent — nothing looks at the date until a later session is compared
 * against it — which is why the decision lives here, on its own, with tests, instead of inline
 * in the eighty-line block that concludes a set.
 *
 * The trap it exists to avoid. On a finished workout the copy the page holds may be a clone of
 * the LIVE program entry, which already carries a date belonging to whichever session concluded
 * most recently. Cloning that into a snapshot would freeze somebody else's date into this
 * session for good, and `classifySessionRecency` would then answer `Latest` for a session a
 * later one has already superseded.
 *
 * It answers a date to USE FOR THE SNAPSHOT ONLY. It must never be written onto the copy that
 * gets attached to the program: on the fill-in path that copy IS the program's next entry, and
 * writing an old workout's date into it rolls the program's own progression clock backwards —
 * the next session then reads a months-long layoff and is offered a reduced target or a
 * strength test.
 *
 * `concludedOnFinishedWorkout` is true when the workout being logged is already finished, which
 * is the only case where the two differ. On the workout in progress the copy's own stamp was
 * written moments ago by this same conclusion and is the more precise of the two.
 */
export function snapshotConcludedDate(
  displayCopyConcluded_utc: string | undefined,
  workoutTime_utc: string | undefined,
  concludedOnFinishedWorkout: boolean,
): string | undefined {
  if (concludedOnFinishedWorkout) {
    return workoutTime_utc ?? displayCopyConcluded_utc;
  }
  return displayCopyConcluded_utc ?? workoutTime_utc;
}

/**
 * #4939: does THIS session still own the exercise's progression?
 *
 * The rule the owner set: the exercise's most recent concluded session owns live progression,
 * until the first set of the NEXT session of that exercise is concluded. So correcting a set in
 * the session you performed most recently still moves your program, and correcting one in an
 * older session does not — that older session's result has already been superseded, and letting
 * it write would drag the program back to where it was before the newer session.
 *
 * How it is decided, with no database read: this session's own date against the date the
 * program last recorded for the exercise. Concluding an exercise's first set normally stamps
 * the program entry's `lastTimeConcluded_utc` and then takes this session's snapshot, so the
 * two carry the same moment until a later session concludes. A session that was skipped and is
 * being filled in afterwards has no snapshot yet, and the caller hands in the workout's own
 * date instead — which answers the same question, because it is still "when did this session
 * happen" measured against "when did this exercise last conclude".
 *
 * Not older, rather than exactly equal, is deliberate. It has to cover the fill-in case, where
 * this session really is the most recent one but has never stamped the program at all.
 *
 * **Snapshots are stamped correctly now, but older ones cannot be trusted.** A snapshot taken
 * on a finished workout used to inherit whichever date the live program entry happened to
 * carry, because the copy it was cloned from came from that entry and `concludeSet` only wrote
 * the stamp when it was absent. This function then answered `Latest` for a session a later one
 * had already superseded. That is fixed: `snapshotConcludedDate` above decides the date and
 * `concludeSet` applies it to the snapshot alone. Nothing needs doing about it again — do not
 * re-implement the stamp.
 *
 * What is NOT fixed, and cannot be: a snapshot written before that change can still carry a
 * borrowed date, and nothing distinguishes those from correct ones. So a `Latest` answer about
 * a session whose snapshot predates this remains unproven, and no migration can repair it —
 * the date that session actually concluded at was never recorded anywhere.
 *
 * The result is NEVER stored on the exercise. Both dates move while the page is open — a
 * conclusion writes one, resetting progress deletes the snapshot — so it is computed where it
 * is used.
 *
 * **Since #5276 this DECIDES something rather than only measuring it.** `onClickSetDone` routes
 * on it through `decideSetTapRoute`: a finished session answering `Latest` re-runs the full
 * conclusion and moves the user's program, and one answering `Superseded` or `Unknown` rewrites
 * only the corrected set. That makes the borrowed-date caveat above a live risk rather than a
 * theoretical one — read it before changing anything here.
 */
export enum SessionRecency {
  /** this session still owns the exercise's progression, so a write is allowed */
  Latest = 'latest',
  /** a later session has concluded this exercise, so this one must not write */
  Superseded = 'superseded',
  /** this session has no date of its own to compare, so nothing is known. The program's date is
   *  not consulted on this path and may well be readable. Callers treat it as Superseded. */
  Unknown = 'unknown',
}

export interface SessionRecencyInput {
  /** when THIS session concluded the exercise: the snapshot's stamp, or the workout's own date
   *  when the session carries no snapshot yet (a skipped exercise being filled in) */
  sessionConcluded_utc: string | undefined;
  /** when the program last recorded a conclusion for this exercise */
  liveLastConcluded_utc: string | undefined;
  /** the exercise this session performed, and the one the program holds now */
  sessionExerciseId: string | undefined;
  liveExerciseId: string | undefined;
}

export function classifySessionRecency(input: SessionRecencyInput): SessionRecency {
  // a different exercise sits in the sub-slot now, so this session's result says nothing about
  // it. `replaceExerciseInSubSlot` clears the stamp when the exercise changes, but
  // `seedProgressionFromBackup` copies a stamp into a freshly created sub-slot, so the dates
  // alone can agree across a swap.
  if (input.sessionExerciseId && input.liveExerciseId
    && input.sessionExerciseId !== input.liveExerciseId) {
    return SessionRecency.Superseded;
  }

  if (!input.sessionConcluded_utc) {
    return SessionRecency.Unknown;
  }

  // the program has never recorded a conclusion for this exercise, so nothing can have
  // superseded this session
  if (!input.liveLastConcluded_utc) {
    return SessionRecency.Latest;
  }

  // ISO-8601 UTC strings compare correctly as text, which is why every stamp in this codebase is
  // written with `toUTC().toISO()`
  return input.sessionConcluded_utc >= input.liveLastConcluded_utc
    ? SessionRecency.Latest
    : SessionRecency.Superseded;
}

/**
 * #4939: work out the four values `classifySessionRecency` compares, for one logged exercise.
 *
 * Split out from the page so the choice of SOURCES can be tested. That choice is the subtle
 * part: this session's date is the snapshot's stamp when there is one, and the workout's own
 * date when there is not — the fill-in case, where the exercise was skipped and is being logged
 * afterwards, so no snapshot exists yet and the session has never stamped the program.
 *
 * `liveProgramSubSlot` is the entry the user's program holds for this exercise NOW, or
 * undefined when it holds none. Undefined means there is nothing to write to, so the answer is
 * `Superseded` rather than a permissive one.
 *
 * Ask BEFORE anything is concluded. Concluding the first set writes both dates the answer is
 * computed from, so asking afterwards asks about the state that conclusion just created.
 */
export function sessionRecencyForLoggedExercise(
  logExercise: Log_WorkoutExercise,
  liveProgramSubSlot: Plan_SubSlot | undefined,
  workoutTime_utc: string | undefined,
): SessionRecency {
  if (!liveProgramSubSlot) {
    return SessionRecency.Superseded;
  }
  return classifySessionRecency({
    sessionConcluded_utc: logExercise?.planSubslotBackup?.lastTimeConcluded_utc ?? workoutTime_utc,
    liveLastConcluded_utc: liveProgramSubSlot.lastTimeConcluded_utc,
    sessionExerciseId: logExercise?.exerciseId,
    liveExerciseId: liveProgramSubSlot.exercise_id,
  });
}

/**
 * #4614 follow-up: build a DISPLAY-ONLY sub-slot for a logged exercise whose program entry is
 * gone and whose workout log carries no snapshot of it.
 *
 * Why this exists. `planSubslotBackup` was added long after the app shipped, so every session
 * logged before it — 2022-era data in the one production database that has been looked at —
 * carries none. Until now those exercises were dropped from the session entirely, so the user
 * saw fewer exercises than they performed, with every logged set still sitting in the document.
 *
 * **It rebuilds, it does not invent.** Everything below comes out of the workout log itself:
 * the slot and its index are parsed out of the sub-slot id exactly as `findOldSubslot` parses
 * them; the exercise is the one the log names; and the progression path is preferably the real
 * `Plan_ExercisePath` a set recorded in `planPathBackup` at conclusion, falling back to the
 * intensity the log names in `pathLevel` plus what the first performed set was actually asked
 * to do. The prescribed set count comes from the log too, so `buildSets` cannot manufacture
 * placeholder sets and flip a finished session back to in-progress (the #3354 shape).
 *
 * **It refuses rather than guesses, and that is the part that matters.** #3321 is the recorded
 * evidence for what a sub-slot with NO usable path does downstream: `rebuildPathsIfNeeded` sees
 * an empty `exercisePaths`, wipes it, and rebuilds a fresh calibration path — so the app offers
 * a strength test to somebody who has been training the exercise all along. A path that is
 * present but missing a target weight or rep count is the neighbouring #3752 shape, which
 * `fixupInvalidPathState` heals into a test AND reports as a problem. So this returns nothing
 * unless the exercise was really performed and the path it produced is one neither of those
 * two will act on. On a refusal the caller drops the exercise exactly as it did before, and
 * reports it — which is strictly better than showing a session that lies about what was asked.
 *
 * Nothing built here is ever written into the program: the caller records
 * `missingFromPlan`, and `writeSubSlotBackToPlan` refuses on it. See
 * `design-docs/exercise-replacement.md`.
 */
export function rebuildDisplaySubSlotFromLog(logExercise: Log_WorkoutExercise): Plan_SubSlot | undefined {
  // a sub-slot id names its slot, and `parseSubslotIdAndOffset` falls back to `$squat` for an id
  // it cannot read — which would silently attribute this exercise to the wrong slot. Refuse.
  if (!logExercise?.subSlotId?.startsWith('$') || !logExercise.exerciseId) {
    return undefined;
  }

  // proof the exercise was really performed. This is also what keeps the rebuild away from an
  // exercise nobody ever started: `mergeWorkoutExercisesWithPlan` keeps one that was removed
  // from the program mid-session, so a live workout can reach here too.
  const performedSets = (logExercise.sets || []).filter(s => s?.state === LogRecordState.Done);
  if (performedSets.length < 1) {
    return undefined;
  }

  const path = rebuildExercisePathFromLog(logExercise, performedSets);
  if (!path) {
    return undefined;
  }

  const { parsedSlotId, initialExerciseIndex } = parseSubslotIdAndOffset(logExercise.subSlotId);
  const intensities = getIntensityForSubSlot(parsedSlotId);
  // pick the training level whose table actually carries this path's intensity, so
  // `getSlotPathIntensity` finds a number instead of falling through to its console.error and
  // a hard-coded 65. The levels do not all carry the same intensities: `Intensified` only
  // exists under Novice, and the TUP levels only under Advanced.
  const trainingLevel = [TrainingLevel.Novice, TrainingLevel.Intermediate, TrainingLevel.Advanced]
    .find(level => intensities[level]?.[path.intensityLevel] !== undefined) ?? TrainingLevel.Advanced;

  return {
    trainingLevel,
    slotId: 'slot/' + parsedSlotId,
    startIndex: initialExerciseIndex,
    exercise_id: logExercise.exerciseId,
    exercisePaths: [path],
    // so `setCurrentPath` selects the one path we built rather than falling back to index 0
    nextWorkoutIntensity: path.intensityLevel,
    intensities,
    // the log is the authority on how many sets this session prescribed
    defaultVolume_sets: logExercise.numPlannedSets ?? logExercise.sets.length,
    // `lastTimeConcluded_utc` is deliberately NOT set. The layoff branch in
    // `loadExerciseAsync` needs both it and `path.lastStats` to fire, and it would cut the
    // target weight of a session that happened years ago as if the user were about to perform
    // it. Leaving this absent keeps that branch inert on a rebuilt sub-slot.
  } as Plan_SubSlot;
}

/**
 * The progression path for a rebuilt sub-slot, or undefined when the log cannot support one
 * that is safe to display. Split out from `rebuildDisplaySubSlotFromLog` so the refusal rule
 * can be read on its own — it is the whole reason the rebuild is safe.
 */
function rebuildExercisePathFromLog(
  logExercise: Log_WorkoutExercise,
  performedSets: Log_WorkoutSet[],
): Plan_ExercisePath | undefined {
  // best source: a real path the app itself wrote at set conclusion. Cloned, because one caller
  // (`generateWorkoutTextAsync`) does not clone the resolved sub-slot and the loader mutates it.
  const recordedPath = logExercise.sets?.find(s => s?.planPathBackup)?.planPathBackup;
  const path: Plan_ExercisePath = recordedPath ? deepClone(recordedPath) : {
    intensityLevel: logExercise.pathLevel ?? IntensityLevel.Initial,
    nextWorkoutSetType: performedSets[0].setType ?? SetType.Regular1st,
    // what the set was ASKED to do, falling back to what it actually did
    target_weight_kg: performedSets[0].plannedWeight_kg ?? performedSets[0].stats?.weight_kg,
    target_reps: performedSets[0].plannedReps ?? performedSets[0].stats?.reps,
  };

  if (!path.intensityLevel || !path.nextWorkoutSetType) {
    return undefined;
  }

  // A path of one of these types legitimately has no target weight — measuring one is what the
  // set is for — and `fixupInvalidPathState` returns early for them without healing or
  // reporting. Every other type must carry both numbers, or that same function would rewrite
  // this path into a test and fire the #3752 canary for a condition nobody can act on.
  //
  // This refusal is worth nothing except as a prediction of what `fixupInvalidPathState`
  // (`workout-loader.service.ts`) will do, so both read the same shared set. Why that set is
  // not the wider `STRENGTH_TEST_SET_TYPES` is written at its definition.
  const fixupSkipsThisPathType = setTypeHasNoTargetPrescription(path.nextWorkoutSetType);
  if (!fixupSkipsThisPathType
    && !(Number.isFinite(path.target_reps) && Number.isFinite(path.target_weight_kg))) {
    return undefined;
  }

  return path;
}
